/**
 * The calendar read's lanes over one row tree (`CalendarEventRead`): the
 * traversal (aggregate constituents, the first reachable facade, the raw
 * lane's stored events) and each lane read once over a range and sliced.
 * SDK-internal; the read is the only caller.
 */
import { NaiveDate, type DateTime, type Utc } from "@tento-chrono";
import {
  EventItem, EventItemAllDay, EventItemTimed, EventOrigin,
  type CalendarEvent as RowsEvent, type JsonObject, type ModelRow, type Option,
} from "@tento-lona/sheets";
import {
  CalendarListEventsProvider, EVENTS_FACADE,
  LIST_AGGREGATE_FACADE, recurringMasterDtstartExcluded,
  type EventsCacheSlots, type EventsFacade, type EventWithSource, type ListAggregateFacade,
} from "@tento-lona/sheets/platform";
import { EventsIndex as RowsEventsIndex, liveConstituentRowsOf } from "@tento-lona/sheets/internal-ui";
import { TimeMembership } from "@tento-lona/sheets/internal-sdk";
import { SourceRow } from "@tento-lona/sheets/platform";
import type { SheetRow } from "../rows/sheet-row.ts";
import { joinedLiveVariantOf } from "../rows/joined-live-variant.ts";

/** The row shape the read walks: a joined row or a rows-side model row. */
export interface ReadNode {
  eventsFacade(): Option<EventsFacade>;
  listAggregate(): Option<ListAggregateFacade>;
  children(): readonly ReadNode[];
  /** The live rows-side row the raw lane reads (`null`: none). */
  variant(): Option<ModelRow>;
  /** The joined handle this node wraps (`null`: a model-row node). */
  joined(): Option<SheetRow.Joined.Handle>;
  /** The live source row this node is (`null`: not a source row). */
  source(): Option<SourceRow>;
  /** The row's attribute bag, decoded (`null`: none). */
  attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A>;
}

export function joinedNode(row: SheetRow.Joined.Handle): ReadNode {
  return {
    eventsFacade: () => row.as(EVENTS_FACADE),
    listAggregate: () => row.as(LIST_AGGREGATE_FACADE),
    children: () => row.children().map(joinedNode),
    variant: () => joinedLiveVariantOf(row),
    joined: () => row,
    source: () => {
      const live = joinedLiveVariantOf(row);
      return live instanceof SourceRow ? live : null;
    },
    attributesAs: <A>(decode: (attributes: JsonObject) => Option<A>) => row.variant?.attributesAs(decode) ?? null,
  };
}

export function modelNode(row: ModelRow): ReadNode {
  return {
    eventsFacade: () => row instanceof SourceRow ? row.as(EVENTS_FACADE) : null,
    listAggregate: () => row instanceof SourceRow ? row.as(LIST_AGGREGATE_FACADE) : null,
    children: () => row.children.map(modelNode),
    variant: () => row,
    joined: () => null,
    source: () => row instanceof SourceRow ? row : null,
    attributesAs: <A>(decode: (attributes: JsonObject) => Option<A>) => row.attributesAs(decode),
  };
}

/** A raw-lane event, wrapped once per span (`null`: not of the lane's kind). */
type RawTimed = { readonly sourced: EventWithSource; readonly item: Option<EventItemTimed> };
type RawAllDay = { readonly sourced: EventWithSource; readonly item: Option<EventItemAllDay> };

/**
 * Every lane of a timed read over one span, each read once. A slice filters
 * them; `first` (the first reachable facade) is read only when a slice's
 * aggregate lane is empty, as the per-range read consults it.
 */
export type TimedLanes = {
  readonly raw: readonly RawTimed[];
  /** Per aggregate constituent, in constituent order. */
  readonly aggregate: readonly (readonly EventItemTimed[])[];
  first: Option<readonly EventItemTimed[]>;
};

/** The all-day counterpart of `TimedLanes`. */
export type AllDayLanes = {
  readonly raw: readonly RawAllDay[];
  aggregate: Option<readonly (readonly EventItemAllDay[])[]>;
  first: Option<readonly EventItemAllDay[]>;
  /** The bind's cache, where the aggregate lane holds its seeded indices (absent: none). */
  readonly cache?: Option<EventsCacheSlots>;
};

/** A month source: a node with an events facade that is not a list aggregate. */
export type MonthSourceNode = {
  readonly row: SourceRow;
  readonly joined: Option<SheetRow.Joined.Handle>;
  readonly facade: EventsFacade;
};

/** The lanes of one read's row tree (`root`), read on demand. */
export class CalendarReadLanes {
  readonly provider = new CalendarListEventsProvider();
  constructor(readonly root: ReadNode) {}

  aggregateListFacade(
    row: ReadNode,
  ): Option<ListAggregateFacade> {
    const aggregate = row.listAggregate();
    if (aggregate) return aggregate;
    for (const child of row.children()) {
      const found = this.aggregateListFacade(child);
      if (found) return found;
    }
    return null;
  }

  /** The first events facade reachable depth-first from `row` (itself first). */
  firstEventsFacade(
    row: ReadNode,
  ): Option<EventsFacade> {
    const facade = row.eventsFacade();
    if (facade) return facade;
    for (const child of row.children()) {
      const found = this.firstEventsFacade(child);
      if (found) return found;
    }
    return null;
  }

  // ── Timed ─────────────────────────────────────────────────────────

  /**
   * Read every timed lane over `range` once: the raw lane (the variant's
   * stored events by day, through `cache`), each aggregate constituent's
   * facade (or a seeded index for a constituent without one, held in
   * `cache` when given).
   */
  timedLanes(range: DateTime.Range<Utc>, cache: Option<EventsCacheSlots>): TimedLanes {
    const raw: RawTimed[] = [];
    const variant = this.root.variant();
    if (variant) {
      const naiveRange = new NaiveDate.Range(range.start.date, range.end.date);
      for (const sourced of this.provider.eventsInRangeWithSource(variant, naiveRange, cache)) {
        const { event, rowId } = sourced;
        let item: Option<EventItemTimed> = null;
        if (!this.provider.isAllDay(event) && !recurringMasterDtstartExcluded(event)) {
          const wrapped = EventItem.of(event, EventOrigin.backingRow(rowId));
          if (wrapped instanceof EventItemTimed) item = wrapped;
        }
        raw.push({ sourced, item });
      }
    }
    const aggregate: (readonly EventItemTimed[])[] = [];
    const aggregateFacade = this.aggregateListFacade(this.root);
    if (aggregateFacade) {
      for (const sourceRow of liveConstituentRowsOf(aggregateFacade)) {
        const facade = sourceRow.as(EVENTS_FACADE);
        aggregate.push(facade ? facade.peekInRange(range) : this.seededIndex(sourceRow, cache)?.peekInRange(range) ?? []);
      }
    }
    return { raw, aggregate, first: null };
  }

  /**
   * `timed(sub)` from lanes read over `range` (`sub` inside it). Raw
   * canonical events win IDs; facade occurrences append in traversal order:
   * aggregate sources, else the first reachable facade (even empty). Facade
   * items are returned as the facades and indices hold them (same identity
   * across reads of an unchanged facade). Answered in the one order range
   * reads use: by start, then by id (an expanded occurrence sits among the
   * stored events by its start).
   */
  timedSlice(lanes: TimedLanes, range: DateTime.Range<Utc>, sub: DateTime.Range<Utc>): readonly EventItemTimed[] {
    const byId = new Map<string, EventItemTimed>();
    const days = new NaiveDate.Range(sub.start.date, sub.end.date);
    for (const { sourced, item } of lanes.raw) {
      if (item == null || byId.has(item.id)) continue;
      if (!this.provider.overlapsDays(sourced.event, days)) continue;
      byId.set(item.id, item);
    }

    // A facade lane answers the items that belong to the range read
    // (`TimeMembership.select`); a sub-range keeps those belonging to it.
    const belongs = (item: EventItemTimed) =>
      TimeMembership.belongsMs(item.range.start, item.range.end, sub.start.mse, sub.end.mse);
    const aggregateItems: EventItemTimed[] = [];
    for (const items of lanes.aggregate) {
      for (const item of items) if (belongs(item)) aggregateItems.push(item);
    }
    let facadeItems: readonly EventItemTimed[] = aggregateItems;
    if (aggregateItems.length === 0) {
      lanes.first ??= this.firstEventsFacade(this.root)?.peekInRange(range) ?? [];
      facadeItems = lanes.first.filter(belongs);
    }
    for (const item of facadeItems) {
      if (!byId.has(item.id)) byId.set(item.id, item);
    }

    // Precedence first (above), then membership: what the raw lane's day
    // buckets return can merely touch `sub`.
    const items: EventItemTimed[] = [];
    for (const item of byId.values()) {
      if (TimeMembership.belongsMs(item.time.mse.start, item.time.mse.end, sub.start.mse, sub.end.mse)) {
        items.push(item);
      }
    }
    return items.sort(byStartThenId);
  }

  // ── All-day ───────────────────────────────────────────────────────

  /** Read the all-day raw lane over `days` once (through `cache`); the
   *  aggregate and facade lanes are read on a slice's first need. */
  allDayLanes(days: NaiveDate.Range, cache: Option<EventsCacheSlots>): AllDayLanes {
    const raw: RawAllDay[] = [];
    const variant = this.root.variant();
    if (variant) {
      for (const sourced of this.provider.allDayEventsInRangeWithSource(variant, days, cache)) {
        const wrapped = EventItem.of(sourced.event, EventOrigin.backingRow(sourced.rowId));
        raw.push({ sourced, item: wrapped instanceof EventItemAllDay ? wrapped : null });
      }
    }
    return { raw, aggregate: null, first: null, cache };
  }

  /**
   * `allDay(sub)` from lanes read over `days` (`sub` inside them): the raw
   * lane when it has a candidate, else the aggregate sources, else the
   * first reachable facade; then the half-open day membership. One item per
   * (id, origin): the same event reached twice from the same source row
   * (two lanes or two paths to one constituent) is answered once, the first
   * reached; the same id from different source rows stays one item per row.
   * By start, then by id (stable: one id's rows keep their reach order).
   */
  allDaySlice(lanes: AllDayLanes, days: NaiveDate.Range, sub: NaiveDate.Range): readonly EventItemAllDay[] {
    let items: readonly EventItemAllDay[] = this.allDayRawSlice(lanes, sub);
    if (items.length === 0) {
      lanes.aggregate ??= this.allDayAggregate(days, lanes.cache ?? null);
      // The index lanes' inclusive lookup (`peekAllDayInRange`): days
      // `start..end` meet `sub.start .. sub.end - 1`.
      const candidate = (item: EventItemAllDay) =>
        item.range.start <= sub.end.dse - 1 && sub.start.dse <= item.range.end;
      items = lanes.aggregate.flatMap((constituent) => constituent.filter(candidate));
      if (items.length === 0) {
        lanes.first ??= this.firstEventsFacade(this.root)?.peekAllDayInRange(days) ?? [];
        items = lanes.first.filter(candidate);
      }
    }
    const start = sub.start.dse;
    const end = sub.end.dse;
    const seen = new Set<string>();
    return items.filter((item) => {
      if (!(item.time.start.dse < end && start <= item.time.end.dse)) return false;
      const key = `${item.id}|${item.origin.backingRowId().toString()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort(byStartThenId);
  }

  /** The raw lane's candidates for `sub` (the provider's inclusive day lookup). */
  allDayRawSlice(lanes: AllDayLanes, sub: NaiveDate.Range): readonly EventItemAllDay[] {
    const items: EventItemAllDay[] = [];
    for (const { sourced, item } of lanes.raw) {
      if (item != null && this.provider.overlapsDays(sourced.event, sub)) items.push(item);
    }
    return items;
  }

  /**
   * The aggregate constituents' all-day items over `days`: each
   * constituent's events facade (its held index: no history scan), or for a
   * constituent without one, an index seeded from its date cells (held in
   * `cache` for the bind, as the timed lane holds its own).
   */
  allDayAggregate(days: NaiveDate.Range, cache: Option<EventsCacheSlots> = null): readonly (readonly EventItemAllDay[])[] {
    const aggregate = this.aggregateListFacade(this.root);
    if (!aggregate) return [];
    const out: (readonly EventItemAllDay[])[] = [];
    for (const sourceRow of liveConstituentRowsOf(aggregate)) {
      const facade = sourceRow.as(EVENTS_FACADE);
      out.push(facade ? facade.peekAllDayInRange(days) : this.seededIndex(sourceRow, cache)?.peekAllDayInRange(days) ?? []);
    }
    return out;
  }

  /**
   * The read-only index of a constituent that has no events facade, seeded
   * from its stored events (`provider.storedEvents`, the copy the raw lane
   * reads too) and answering both the timed and the all-day lane, held in
   * `cache` for the bind when one is given. Without one it is still
   * reused while the row's cells are unchanged (`seededRevision`, O(1)), so
   * a facade-less constituent is not re-seeded on every read. `null`: no
   * events. A seeded index answers stored events only: it does not expand
   * recurring masters (`EventsIndex.seeded`); a constituent with masters
   * needs an events facade.
   */
  seededIndex(sourceRow: SourceRow, cache: Option<EventsCacheSlots>): Option<RowsEventsIndex> {
    const slot = `calendar-read/seeded/${sourceRow.id.toString()}`;
    const held = cache?.slots.get(slot);
    if (held === null || held instanceof RowsEventsIndex) return held;
    const revision = CalendarReadLanes.seededRevision(sourceRow);
    const kept = this.#seeded.get(sourceRow);
    if (kept != null && CalendarReadLanes.#same(kept.revision, revision)) {
      cache?.slots.set(slot, kept.index);
      return kept.index;
    }
    const events: readonly RowsEvent[] = this.provider.storedEvents(sourceRow);
    const index = events.length === 0 ? null : RowsEventsIndex.seeded([...events], sourceRow.eventOrigin());
    this.#seeded.set(sourceRow, { revision, index });
    cache?.slots.set(slot, index);
    return index;
  }

  /** The seeded index of each facade-less constituent, with the revision it was seeded at. */
  readonly #seeded = new WeakMap<SourceRow, { readonly revision: readonly unknown[]; readonly index: Option<RowsEventsIndex> }>();

  /**
   * A facade-less source row's revision: its cell store's data and change
   * counter (`SourceRow.cellsRevision`), O(1): no cell is read, so a
   * `generation()` costs the same at any history size. Equal revisions mean
   * the same events.
   */
  static seededRevision(sourceRow: SourceRow): readonly unknown[] {
    return sourceRow.cellsRevision();
  }

  static #same(a: readonly unknown[], b: readonly unknown[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  /**
   * The month's sources: walking from the root, a row with an events facade
   * that is not a list aggregate is one, and the walk does not descend below
   * it; any other row's children are walked in order.
   */
  monthSources(): readonly MonthSourceNode[] {
    const out: MonthSourceNode[] = [];
    const walk = (node: ReadNode): void => {
      const facade = node.eventsFacade();
      const row = node.source();
      if (facade != null && node.listAggregate() == null && row != null) {
        out.push({ row, joined: node.joined(), facade });
        return;
      }
      for (const child of node.children()) walk(child);
    };
    walk(this.root);
    return out;
  }
}

/** The order range reads answer in: by start, then by id. */
function byStartThenId(a: EventItemTimed | EventItemAllDay, b: EventItemTimed | EventItemAllDay): number {
  return a.range.start - b.range.start || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
